import { useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ShieldCheck, ArrowRight, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import AuthBackdrop from "@/components/AuthBackdrop";

const EASE = [0.16, 1, 0.3, 1];

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const reduce = useReducedMotion();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const rise = useMemo(
    () => ({
      hidden: reduce ? { opacity: 1 } : { opacity: 0, y: 14 },
      show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
    }),
    [reduce],
  );

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(email, password, name);
      toast.success("Account created");
      nav("/");
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const field =
    "w-full rounded-md border border-zinc-800 bg-zinc-900/60 px-3.5 py-2.5 text-sm text-zinc-100 transition-colors duration-200 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30";
  const labelCls = "font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500";

  return (
    <div className="relative flex min-h-[100dvh] w-full items-center justify-center overflow-hidden bg-[#09090B] px-6 py-14 text-zinc-50">
      <AuthBackdrop centered />

      <motion.div
        initial="hidden"
        animate="show"
        variants={{ show: { transition: { staggerChildren: reduce ? 0 : 0.08, delayChildren: reduce ? 0 : 0.12 } } }}
        className="relative z-10 w-full max-w-sm"
      >
        <motion.div variants={rise} className="mb-9 flex items-center gap-3">
          <span className="grid h-11 w-11 place-items-center rounded-md bg-gradient-to-br from-blue-600 to-sky-400 shadow-[0_8px_28px_-10px_rgba(37,99,235,0.9)]">
            <ShieldCheck size={20} strokeWidth={2} className="text-white" />
          </span>
          <span>
            <span className="block font-display text-[17px] font-bold leading-tight tracking-tight">AegisOne RMM</span>
            <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              Managed Security Operations
            </span>
          </span>
        </motion.div>

        <motion.div variants={rise}>
          <h1 className="font-display text-2xl font-semibold tracking-tight">Request operator access</h1>
          <p className="mt-2 text-sm text-zinc-500">
            New accounts start at L1 Technician. Roles are raised by your MSP owner.
          </p>
        </motion.div>

        <form onSubmit={submit} className="mt-8 space-y-5" noValidate>
          <motion.div variants={rise} className="flex flex-col gap-2">
            <label htmlFor="reg-name" className={labelCls}>
              Full name
            </label>
            <input
              id="reg-name"
              data-testid="reg-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
              required
              className={field}
            />
          </motion.div>

          <motion.div variants={rise} className="flex flex-col gap-2">
            <label htmlFor="reg-email" className={labelCls}>
              Work email
            </label>
            <input
              id="reg-email"
              data-testid="reg-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
              className={field}
            />
          </motion.div>

          <motion.div variants={rise} className="flex flex-col gap-2">
            <label htmlFor="reg-password" className={labelCls}>
              Password
            </label>
            <input
              id="reg-password"
              data-testid="reg-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
              className={field}
            />
          </motion.div>

          <AnimatePresence initial={false}>
            {error ? (
              <motion.p
                key="err"
                role="alert"
                initial={reduce ? false : { opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.25, ease: EASE }}
                className="flex items-start gap-2 overflow-hidden text-[13px] text-red-400"
              >
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </motion.p>
            ) : null}
          </AnimatePresence>

          <motion.button
            variants={rise}
            data-testid="reg-submit"
            type="submit"
            disabled={loading}
            whileHover={reduce || loading ? undefined : { y: -1 }}
            whileTap={reduce || loading ? undefined : { scale: 0.985 }}
            className="group inline-flex w-full items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-[0_10px_30px_-12px_rgba(37,99,235,0.9)] transition-colors duration-200 hover:bg-blue-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/60 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {loading ? (
              <>
                <Loader2 size={15} className="animate-spin" />
                Creating
              </>
            ) : (
              <>
                Create account
                <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-0.5" />
              </>
            )}
          </motion.button>
        </form>

        <motion.p variants={rise} className="mt-8 border-t border-zinc-800/80 pt-5 text-[13px] text-zinc-500">
          Have an account?{" "}
          <Link to="/login" className="text-blue-400 underline-offset-4 hover:underline">
            Sign in
          </Link>
        </motion.p>
      </motion.div>
    </div>
  );
}
